/**
 * Lot CYC (V650 ; registre n° 13) — gestes de cycle de vie d'UN projet, partagés par Organisation ›
 * Projets (liste de l'administration) et la Vue projet : renommer, archiver (confirmé), désarchiver
 * (confirmé), supprimer (administration de l'organisation : export complet puis purge différée,
 * annulable, confirmation par le NOM du projet).
 *
 * L'interface PROPOSE selon le rôle ; la passerelle juge et ses refus nommés s'affichent tels quels.
 */
import { useId, useState, type FormEvent, type JSX } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Workspace } from '@/api/client'
import { codeErreur } from '@/api/niveauxApi'
import {
  CLE_ETAT_CYCLE, CLE_PROJETS_ADMIN, cycleDeVieApi, estAdministrationOrganisation, type DemandeSuppression,
  type ReglagesCycle,
} from '@/api/cycleDeVieApi'
import { useSession } from '@/hooks/useSession'
import { useWorkspace } from '@/hooks/useWorkspace'
import { useConfirmation } from '@/components/ConfirmationProvider'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useT } from '@/i18n/language'
import { formaterInstant } from '@/lib/arretIA'
import { LIEN_CIBLE } from '@/lib/cibles'

type T = ReturnType<typeof useT>['t']

export const REGLAGES_PAR_DEFAUT: ReglagesCycle = { delay_days_default: 30, delay_days_min: 7, delay_days_max: 365 }

/** Phrase d'un refus NOMMÉ de la passerelle (lot CYC). */
export function messageRefusCycle(t: T, code: string | null | undefined): string {
  switch (code) {
    case 'ORG_ADMIN_REQUIRED': return t('cycle.refus.adminOrg', 'Réservé à l’administration de l’organisation.')
    case 'PLATFORM_ADMIN_REQUIRED': return t('cycle.refus.adminPlateforme', 'Réservé à l’administration de la plateforme.')
    case 'PROJECT_LIFECYCLE_FORBIDDEN': return t('cycle.refus.proprietaire', 'Réservé au propriétaire du projet ou à l’administration de l’organisation.')
    case 'LIFECYCLE_CONFIRMATION_MISMATCH': return t('cycle.refus.confirmation', 'La confirmation ne correspond pas : tapez le nom exact.')
    case 'LIFECYCLE_REASON_REQUIRED': return t('cycle.refus.motif', 'Un motif de 10 à 1000 caractères est requis.')
    case 'LIFECYCLE_DELAY_OUT_OF_RANGE': return t('cycle.refus.delai', 'Délai hors des bornes permises.')
    case 'LIFECYCLE_ALREADY_SCHEDULED': return t('cycle.refus.dejaProgrammee', 'Une suppression est déjà programmée.')
    case 'LIFECYCLE_REQUEST_CLOSED': return t('cycle.refus.close', 'Cette demande est close : plus rien à annuler.')
    case 'WORKSPACE_DELETION_PENDING': return t('cycle.refus.suppressionEnCours', 'Suppression programmée : annulez-la d’abord.')
    case 'WORKSPACE_NOT_ARCHIVED': return t('cycle.refus.pasArchive', 'Ce projet n’est pas archivé.')
    case 'WORKSPACE_ARCHIVED': return t('cycle.refus.archive', 'Projet archivé : désarchivez-le d’abord.')
    case 'ORGANISATION_SELF_DELETION_FORBIDDEN': return t('cycle.refus.soiMeme', 'La plateforme ne supprime jamais sa propre organisation.')
    case 'ORGANISATION_DELETED': return t('cycle.refus.supprimee', 'Organisation déjà supprimée.')
    case 'ORGANISATION_SUSPENDED': return t('cycle.refus.suspendue', 'Organisation suspendue : lecture seule.')
    case 'LIFECYCLE_EXPORT_NOT_READY': return t('cycle.refus.exportPasPret', 'L’archive n’est pas encore prête.')
    case 'RESOURCE_UNAVAILABLE': return t('cycle.refus.introuvable', 'Introuvable.')
    case 'VALIDATION_ERROR': return t('cycle.refus.validation', 'Valeur refusée par la passerelle.')
    default: return t('cycle.refus.inattendu', 'La passerelle a refusé l’action.')
  }
}

export interface ProjetCycle {
  workspace_id: string
  name: string
  description: string | null
  status: string
  deletion_request_id: number | null
}

/** Formulaire commun de suppression (projet ou organisation) : motif, délai, confirmation tapée. */
export function FormulaireSuppression({ attendu, libelleConfirmation, reglages, enCours, erreur, onSoumettre, onAnnuler, testid }: {
  attendu: string
  libelleConfirmation: string
  reglages: ReglagesCycle
  enCours: boolean
  erreur: string | null
  onSoumettre: (corps: { reason: string; delay_days: number; confirmation: string }) => void
  onAnnuler: () => void
  testid: string
}): JSX.Element {
  const { t } = useT()
  const [motif, setMotif] = useState('')
  const [delai, setDelai] = useState(String(reglages.delay_days_default))
  const [confirmation, setConfirmation] = useState('')
  const [local, setLocal] = useState<string | null>(null)
  const idMotif = useId()
  const idDelai = useId()
  const idConfirmation = useId()
  const soumettre = (e: FormEvent): void => {
    e.preventDefault()
    const jours = Number(delai)
    if (motif.trim().length < 10) { setLocal(messageRefusCycle(t, 'LIFECYCLE_REASON_REQUIRED')); return }
    if (!Number.isInteger(jours) || jours < reglages.delay_days_min || jours > reglages.delay_days_max) {
      setLocal(messageRefusCycle(t, 'LIFECYCLE_DELAY_OUT_OF_RANGE')); return
    }
    if (confirmation.trim() !== attendu) { setLocal(messageRefusCycle(t, 'LIFECYCLE_CONFIRMATION_MISMATCH')); return }
    setLocal(null)
    onSoumettre({ reason: motif.trim(), delay_days: jours, confirmation: confirmation.trim() })
  }
  const message = local ?? erreur
  return (
    <form onSubmit={soumettre} className="space-y-3 text-sm" noValidate data-testid={testid}>
      <div className="space-y-1">
        <Label htmlFor={idMotif}>{t('cycle.motif', 'Motif (obligatoire, écrit sur la demande, à l’audit et dans la notification)')}</Label>
        <Textarea id={idMotif} value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={1000}
                  data-testid={`${testid}-motif`} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={idDelai}>
          {t('cycle.delai', 'Délai avant la purge définitive, en jours ({{min}} à {{max}})', { min: reglages.delay_days_min, max: reglages.delay_days_max })}
        </Label>
        <Input id={idDelai} type="number" inputMode="numeric" min={reglages.delay_days_min} max={reglages.delay_days_max}
               value={delai} onChange={(e) => setDelai(e.target.value)} className="w-32" data-testid={`${testid}-delai`} />
      </div>
      <div className="space-y-1">
        <Label htmlFor={idConfirmation}>{libelleConfirmation}</Label>
        <Input id={idConfirmation} value={confirmation} onChange={(e) => setConfirmation(e.target.value)} autoComplete="off"
               data-testid={`${testid}-confirmation`} />
      </div>
      {message ? <p role="alert" className="text-[var(--color-error-text)]" data-testid={`${testid}-erreur`}>{message}</p> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onAnnuler}>{t('cycle.fermer', 'Fermer')}</Button>
        <Button type="submit" variant="destructive" disabled={enCours} aria-busy={enCours} data-testid={`${testid}-valider`}>
          {t('cycle.programmerSuppression', 'Programmer la suppression')}
        </Button>
      </DialogFooter>
    </form>
  )
}

/** Ce qui va se passer, dit avant de demander quoi que ce soit. */
export function ConsequencesSuppression({ portee, delai }: { portee: 'projet' | 'organisation'; delai: number }): JSX.Element {
  const { t } = useT()
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm" data-testid="cycle-consequences">
      <li>{t('cycle.consequence.export', 'Un export complet est produit d’abord : données, artefacts, preuves et journal, en une archive téléchargeable avec son empreinte SHA-256.')}</li>
      <li>{portee === 'projet'
        ? t('cycle.consequence.lectureSeuleProjet', 'Le projet passe aussitôt en lecture seule ; ses missions en cours sont arrêtées.')
        : t('cycle.consequence.lectureSeuleOrganisation', 'L’organisation est aussitôt suspendue : ses membres passent en lecture seule ; ses missions sont arrêtées.')}</li>
      <li>{t('cycle.consequence.delai', 'La purge définitive a lieu après {{n}} jour(s), seulement si l’export est prêt. D’ici là, la suppression s’annule.', { n: delai })}</li>
      <li>{portee === 'projet'
        ? t('cycle.consequence.notificationProjet', 'Les propriétaires du projet sont notifiés ; tout est écrit à l’audit de l’organisation.')
        : t('cycle.consequence.notificationOrganisation', 'Les propriétaires de projets de l’organisation sont notifiés ; tout est écrit au journal de la plateforme. Les comptes de ses utilisateurs seront supprimés à la purge.')}</li>
    </ul>
  )
}

export function ActionsCycleProjet({ projet, peutGerer, peutSupprimer, reglages = REGLAGES_PAR_DEFAUT, compact = false }: {
  projet: ProjetCycle
  /** Propriétaire du projet ou administration de l'organisation. */
  peutGerer: boolean
  /** Administration de l'organisation. */
  peutSupprimer: boolean
  reglages?: ReglagesCycle
  compact?: boolean
}): JSX.Element | null {
  const { t } = useT()
  const client = useQueryClient()
  const confirmer = useConfirmation()
  const [dialogue, setDialogue] = useState<'renommer' | 'supprimer' | null>(null)
  const [nom, setNom] = useState(projet.name)
  const [description, setDescription] = useState(projet.description ?? '')
  const [demande, setDemande] = useState<DemandeSuppression | null>(null)
  const idNom = useId()
  const idDescription = useId()

  const rafraichir = async (): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: CLE_PROJETS_ADMIN }),
      client.invalidateQueries({ queryKey: ['workspaces'] }),
      client.invalidateQueries({ queryKey: CLE_ETAT_CYCLE }),
      client.invalidateQueries({ queryKey: ['portfolio'] }),
    ])
  }
  const renommer = useMutation({
    mutationFn: () => cycleDeVieApi.renommer(projet.workspace_id, nom.trim(), description.trim() || null),
    onSuccess: async () => { await rafraichir(); setDialogue(null) },
    meta: { retour: 'insitu' },
  })
  const basculer = useMutation({
    mutationFn: (archiver: boolean) => archiver ? cycleDeVieApi.archiver(projet.workspace_id) : cycleDeVieApi.desarchiver(projet.workspace_id),
    onSuccess: rafraichir,
    meta: { retour: 'insitu' },
  })
  const supprimer = useMutation({
    mutationFn: (corps: { reason: string; delay_days: number; confirmation: string }) => cycleDeVieApi.supprimerProjet(projet.workspace_id, corps),
    onSuccess: async (d) => { setDemande(d); await rafraichir() },
    meta: { retour: 'insitu' },
  })

  if (!peutGerer && !peutSupprimer) return null
  const archive = projet.status === 'archived'
  const enSuppression = projet.deletion_request_id != null

  const archiverConfirme = async (): Promise<void> => {
    const r = await confirmer({
      titre: t('cycle.archiverTitre', 'Archiver « {{nom}} » ?', { nom: projet.name }),
      consequences: [
        t('cycle.archiverEffet1', 'Le projet passe en lecture seule : aucune écriture, aucune nouvelle mission.'),
        t('cycle.archiverEffet2', 'Ses données, son journal et ses membres sont conservés.'),
      ],
      reversibilite: t('cycle.archiverReversible', 'Désarchivable à tout moment par son propriétaire ou l’administration de l’organisation.'),
      verbe: t('cycle.archiver', 'Archiver'),
      ton: 'neutre',
      testId: 'cycle-confirmer-archivage',
    })
    if (r.confirme) basculer.mutate(true)
  }
  const desarchiverConfirme = async (): Promise<void> => {
    const r = await confirmer({
      titre: t('cycle.desarchiverTitre', 'Désarchiver « {{nom}} » ?', { nom: projet.name }),
      consequences: [t('cycle.desarchiverEffet', 'Le projet redevient modifiable par ses membres.')],
      reversibilite: t('cycle.desarchiverReversible', 'Il peut être de nouveau archivé à tout moment.'),
      verbe: t('cycle.desarchiver', 'Désarchiver'),
      ton: 'neutre',
      testId: 'cycle-confirmer-desarchivage',
    })
    if (r.confirme) basculer.mutate(false)
  }

  const taille = compact ? 'sm' : 'default'
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`cycle-actions-${projet.workspace_id}`}>
      {enSuppression ? (
        <Link to={`/org/suppressions/${projet.deletion_request_id}`} className={`text-sm font-medium ${LIEN_CIBLE}`}
              data-testid={`cycle-lien-demande-${projet.workspace_id}`}>
          {t('cycle.voirDemande', 'Suppression programmée — voir la demande')}<span className="sr-only"> {projet.name}</span>
        </Link>
      ) : (
        <>
          {peutGerer && !archive ? (
            <Button type="button" size={taille} variant="outline" onClick={() => { setNom(projet.name); setDescription(projet.description ?? ''); renommer.reset(); setDialogue('renommer') }}
                    data-testid={`cycle-renommer-${projet.workspace_id}`}>
              {t('cycle.renommer', 'Renommer…')}<span className="sr-only"> {projet.name}</span>
            </Button>
          ) : null}
          {peutGerer ? (
            <Button type="button" size={taille} variant="outline" disabled={basculer.isPending}
                    onClick={() => void (archive ? desarchiverConfirme() : archiverConfirme())}
                    data-testid={`cycle-${archive ? 'desarchiver' : 'archiver'}-${projet.workspace_id}`}>
              {archive ? t('cycle.desarchiverAction', 'Désarchiver…') : t('cycle.archiverAction', 'Archiver…')}<span className="sr-only"> {projet.name}</span>
            </Button>
          ) : null}
          {peutSupprimer ? (
            <Button type="button" size={taille} variant="destructive" onClick={() => { supprimer.reset(); setDemande(null); setDialogue('supprimer') }}
                    data-testid={`cycle-supprimer-${projet.workspace_id}`}>
              {t('cycle.supprimerAction', 'Supprimer…')}<span className="sr-only"> {projet.name}</span>
            </Button>
          ) : null}
        </>
      )}
      {basculer.isError ? (
        <p role="alert" className="w-full text-sm text-[var(--color-error-text)]" data-testid={`cycle-erreur-${projet.workspace_id}`}>
          {messageRefusCycle(t, codeErreur(basculer.error))}
        </p>
      ) : null}

      {dialogue === 'renommer' ? (
        <Dialog open onOpenChange={(o) => { if (!o) setDialogue(null) }}>
          <DialogContent className="sm:max-w-lg" data-testid="cycle-dialogue-renommer">
            <DialogHeader>
              <DialogTitle>{t('cycle.renommerTitre', 'Renommer « {{nom}} »', { nom: projet.name })}</DialogTitle>
              <DialogDescription>{t('cycle.renommerAide', 'Le nouveau nom s’affiche partout ; l’identifiant du projet ne change pas. Écrit à l’audit.')}</DialogDescription>
            </DialogHeader>
            <form className="space-y-3 text-sm" noValidate
                  onSubmit={(e) => { e.preventDefault(); if (nom.trim().length >= 3) renommer.mutate() }}>
              <div className="space-y-1">
                <Label htmlFor={idNom}>{t('cycle.nom', 'Nom (3 à 255 caractères)')}</Label>
                <Input id={idNom} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={255} data-testid="cycle-renommer-nom" />
              </div>
              <div className="space-y-1">
                <Label htmlFor={idDescription}>{t('cycle.description', 'Description')}</Label>
                <Textarea id={idDescription} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000}
                          data-testid="cycle-renommer-description" />
              </div>
              {nom.trim().length < 3 ? <p role="alert" className="text-[var(--color-error-text)]" data-testid="cycle-renommer-court">{t('cycle.nomCourt', 'Le nom doit comporter au moins 3 caractères.')}</p> : null}
              {renommer.isError ? (
                <p role="alert" className="text-[var(--color-error-text)]" data-testid="cycle-renommer-erreur">{messageRefusCycle(t, codeErreur(renommer.error))}</p>
              ) : null}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogue(null)}>{t('cycle.fermer', 'Fermer')}</Button>
                <Button type="submit" disabled={renommer.isPending} aria-busy={renommer.isPending} data-testid="cycle-renommer-valider">
                  {t('cycle.renommerValider', 'Renommer')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      ) : null}

      {dialogue === 'supprimer' ? (
        <Dialog open onOpenChange={(o) => { if (!o) setDialogue(null) }}>
          <DialogContent className="sm:max-w-xl" data-testid="cycle-dialogue-supprimer">
            <DialogHeader>
              <DialogTitle>{t('cycle.supprimerTitre', 'Supprimer le projet « {{nom}} »', { nom: projet.name })}</DialogTitle>
              <DialogDescription>{t('cycle.supprimerAide', 'Suppression différée et annulable : rien n’est effacé avant la fin du délai.')}</DialogDescription>
            </DialogHeader>
            {demande ? (
              <div className="space-y-2 text-sm">
                <p role="status" data-testid="cycle-supprimer-ok">
                  {t('cycle.supprimerOk', 'Suppression programmée : purge après le {{quand}}. L’export complet se prépare.', { quand: formaterInstant(demande.purge_after) ?? demande.purge_after })}
                </p>
                <Link to={`/org/suppressions/${demande.id}`} className={`font-medium ${LIEN_CIBLE}`} data-testid="cycle-supprimer-lien">
                  {t('cycle.ouvrirDemande', 'Ouvrir la demande n° {{id}}', { id: demande.id })}
                </Link>
                <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogue(null)}>{t('cycle.fermer', 'Fermer')}</Button></DialogFooter>
              </div>
            ) : (
              <>
                <ConsequencesSuppression portee="projet" delai={reglages.delay_days_default} />
                <FormulaireSuppression attendu={projet.name} reglages={reglages} enCours={supprimer.isPending}
                  libelleConfirmation={t('cycle.confirmationProjet', 'Pour confirmer, tapez le nom exact du projet : {{nom}}', { nom: projet.name })}
                  erreur={supprimer.isError ? messageRefusCycle(t, codeErreur(supprimer.error)) : null}
                  onSoumettre={(c) => supprimer.mutate(c)} onAnnuler={() => setDialogue(null)} testid="cycle-supprimer" />
              </>
            )}
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  )
}

/**
 * Les gestes de cycle de vie du PROJET ACTIF, pour la Vue projet (« la fiche projet ») : propriétaire
 * du projet ou administration de l'organisation ; rien pour les autres rôles.
 */
export function GestesCycleProjetActif({ projet }: { projet?: Workspace } = {}): JSX.Element | null {
  // Intégration CYC × NAV : la porte « projet archivé » (`ProjetArchive`) passe le projet qu'elle
  // garde ; sinon, le projet actif de la Vue projet.
  const { active: actif } = useWorkspace()
  const active = projet ?? actif
  const session = useSession()
  const administration = estAdministrationOrganisation(session)
  const etat = useQuery({ queryKey: [...CLE_ETAT_CYCLE, session?.organisationId], queryFn: cycleDeVieApi.etat,
    enabled: Boolean(active && session?.organisationId), retry: false })
  if (!active) return null
  const peutGerer = administration || active.membership_role === 'owner'
  if (!peutGerer) return null
  const demande = (etat.data?.projects ?? []).find((p) => p.workspace_id === active.workspace_id)
  return (
    <ActionsCycleProjet compact peutGerer={peutGerer} peutSupprimer={administration}
      projet={{ workspace_id: active.workspace_id, name: active.name, description: active.description ?? null,
        status: active.status ?? 'active', deletion_request_id: demande?.id ?? null }} />
  )
}

/**
 * Les gestes d'un projet dont l'appelant est PROPRIÉTAIRE, dans la liste « mes projets » d'Organisation ›
 * Projets (niveau organisation : toujours joignable, même quand le projet archivé n'ouvre plus ses
 * écrans) — c'est là qu'un propriétaire qui n'administre pas l'organisation le désarchive.
 */
export function GestesCycleProjetMembre({ projet }: {
  projet: { workspace_id: string; name: string; description?: string | null; status?: string; membership_role?: string | null }
}): JSX.Element | null {
  const session = useSession()
  const etat = useQuery({ queryKey: [...CLE_ETAT_CYCLE, session?.organisationId], queryFn: cycleDeVieApi.etat,
    enabled: projet.membership_role === 'owner', retry: false })
  if (projet.membership_role !== 'owner') return null
  const demande = (etat.data?.projects ?? []).find((p) => p.workspace_id === projet.workspace_id)
  return (
    <ActionsCycleProjet compact peutGerer peutSupprimer={false}
      projet={{ workspace_id: projet.workspace_id, name: projet.name, description: projet.description ?? null,
        status: projet.status ?? 'active', deletion_request_id: demande?.id ?? null }} />
  )
}
